import type { RawImpedanceResult, RawImpedancePolar, LabCloudExportFile, LabCloudExportEntry, LabCloudExportMeasurement, LabCloudExportChannel, LabCloudExportPoint } from './types';

export interface DevMessageFile {
  filename: string;
  uid: string;
  version: {
    hardware: string;
    firmware: string;
    serialNumber: string;
  };
  state: {
    temperatureC: number;
    humidityPct: number;
    batteryPct: number;
    batteryV: number;
    powerState: string;
    operatingTimeMin: number;
  };
  durationMs: number;
  errorCode: number;
  timestampMs: number;
  channelIds: string[];
  impedanceResult: RawImpedanceResult;
}

export interface ParseError {
  filename: string;
  reason: string;
}

export type ParseResult =
  | { ok: true; data: DevMessageFile }
  | { ok: false; error: ParseError };

function fail(filename: string, reason: string): ParseResult {
  return { ok: false, error: { filename, reason } };
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isNumberArray(v: unknown): v is number[] {
  return Array.isArray(v) && v.every((x) => typeof x === 'number');
}

function isPolarArray(v: unknown): v is RawImpedancePolar[] {
  return (
    Array.isArray(v) &&
    v.every(
      (x) =>
        isObject(x) &&
        typeof x['magnitude_Ohm'] === 'number' &&
        typeof x['phase_rad'] === 'number',
    )
  );
}

/**
 * Parses a raw DevMessage JSON file (as unknown) into a typed DevMessageFile.
 *
 * Validation rules:
 * - Required fields: uid, impedance_result, app_message
 * - error_code must equal 0
 * - impedance_result.values.length > 0
 * - impedance_result.frequency_Hz.length > 0
 * - Channels extracted from app_message.impedance_exec.channels
 */
export function parseDevMessageFile(
  filename: string,
  json: unknown,
): ParseResult {
  if (!isObject(json)) {
    return fail(filename, 'JSON root must be an object.');
  }

  // uid
  if (typeof json['uid'] !== 'string' || json['uid'].length === 0) {
    return fail(filename, 'Missing or invalid field: uid');
  }

  // error_code
  const errorCode = json['error_code'];
  if (typeof errorCode !== 'number') {
    return fail(filename, 'Missing or invalid field: error_code');
  }
  if (errorCode !== 0) {
    return fail(filename, `error_code is ${errorCode} (must be 0 to be valid).`);
  }

  // app_message
  if (!isObject(json['app_message'])) {
    return fail(filename, 'Missing or invalid field: app_message');
  }
  const appMsg = json['app_message'] as Record<string, unknown>;

  // app_message.timestamp_ms
  const timestampMs =
    typeof appMsg['timestamp_ms'] === 'number' ? appMsg['timestamp_ms'] : 0;

  // app_message.impedance_exec.channels
  let channelIds: string[] = [];
  if (isObject(appMsg['impedance_exec'])) {
    const exec = appMsg['impedance_exec'] as Record<string, unknown>;
    if (Array.isArray(exec['channels'])) {
      channelIds = (exec['channels'] as unknown[])
        .filter((c) => typeof c === 'string' || typeof c === 'number')
        .map((c, i) => {
          if (typeof c === 'string') return c;
          // numeric enum → default label
          return `CH${i + 1}`;
        });
    }
  }
  // Fall back to 4 channels if exec config is absent
  if (channelIds.length === 0) {
    channelIds = ['CH1', 'CH2', 'CH3', 'CH4'];
  }

  // impedance_result
  if (!isObject(json['impedance_result'])) {
    return fail(filename, 'Missing or invalid field: impedance_result');
  }
  const ir = json['impedance_result'] as Record<string, unknown>;

  if (!isNumberArray(ir['frequency_Hz']) || ir['frequency_Hz'].length === 0) {
    return fail(
      filename,
      'impedance_result.frequency_Hz must be a non-empty number array.',
    );
  }

  if (!isPolarArray(ir['values']) || ir['values'].length === 0) {
    return fail(
      filename,
      'impedance_result.values must be a non-empty array of { magnitude_Ohm, phase_rad }.',
    );
  }

  const impedanceResult: RawImpedanceResult = {
    frequency_Hz: ir['frequency_Hz'],
    values: ir['values'],
  };

  // version (optional fields — extract with safe fallback)
  const ver = isObject(json['version']) ? (json['version'] as Record<string, unknown>) : {};
  const version = {
    hardware: typeof ver['hardware'] === 'string' ? ver['hardware'] : '',
    firmware: typeof ver['firmware'] === 'string' ? ver['firmware'] : '',
    serialNumber:
      typeof ver['serial_number'] === 'string' ? ver['serial_number'] : '',
  };

  // state
  const st = isObject(json['state']) ? (json['state'] as Record<string, unknown>) : {};
  const state = {
    temperatureC: typeof st['temp'] === 'number' ? st['temp'] : 0,
    humidityPct: typeof st['humidity'] === 'number' ? st['humidity'] : 0,
    batteryPct:
      typeof st['battery_pct'] === 'number' ? st['battery_pct'] : 0,
    batteryV: typeof st['battery_v'] === 'number' ? st['battery_v'] : 0,
    powerState:
      typeof st['power_state'] === 'string' ? st['power_state'] : '',
    operatingTimeMin:
      typeof st['operating_time'] === 'number' ? st['operating_time'] : 0,
  };

  const durationMs =
    typeof json['duration_ms'] === 'number' ? json['duration_ms'] : 0;

  return {
    ok: true,
    data: {
      filename,
      uid: json['uid'] as string,
      version,
      state,
      durationMs,
      errorCode,
      timestampMs,
      channelIds,
      impedanceResult,
    },
  };
}

// ---------------------------------------------------------------------------
// LabCloud app-native export format parser
// ---------------------------------------------------------------------------

function parseExportMeasurement(v: unknown): LabCloudExportMeasurement | null {
  if (!isObject(v)) return null;
  const obj = v as Record<string, unknown>;
  if (!Array.isArray(obj['channels'])) return null;

  const channels: LabCloudExportChannel[] = [];
  for (const ch of obj['channels'] as unknown[]) {
    if (!isObject(ch)) continue;
    const chObj = ch as Record<string, unknown>;
    if (typeof chObj['channelId'] !== 'string') continue;
    if (!Array.isArray(chObj['points'])) continue;

    const points: LabCloudExportPoint[] = [];
    for (const pt of chObj['points'] as unknown[]) {
      if (!isObject(pt)) continue;
      const ptObj = pt as Record<string, unknown>;
      if (
        typeof ptObj['frequency'] !== 'number' ||
        typeof ptObj['magnitude'] !== 'number' ||
        typeof ptObj['phase_rad'] !== 'number'
      ) continue;
      points.push({
        frequency: ptObj['frequency'] as number,
        magnitude: ptObj['magnitude'] as number,
        phase_rad: ptObj['phase_rad'] as number,
      });
    }

    if (points.length > 0) {
      channels.push({ channelId: chObj['channelId'] as string, points });
    }
  }

  return channels.length > 0 ? { channels } : null;
}

/**
 * Detects and parses a LabCloud app-native export JSON file.
 *
 * A LabCloud export is identified by the presence of `metadata.exportedAt` and a `tests` array.
 * Delta-mode exports are rejected because they only contain derived data (no raw specimen measurements).
 * Tests missing either baseline or specimen data are silently skipped.
 */
export function parseLabCloudExportFile(
  filename: string,
  json: unknown,
): { ok: true; data: LabCloudExportFile } | { ok: false; error: ParseError } {
  const err = (reason: string): { ok: false; error: ParseError } =>
    ({ ok: false, error: { filename, reason } });

  if (!isObject(json)) return err('JSON root must be an object.');

  // Distinguish from DevMessage format by requiring metadata.exportedAt + tests[]
  if (!isObject(json['metadata']) || typeof (json['metadata'] as Record<string, unknown>)['exportedAt'] !== 'string') {
    return err('Not a LabCloud export: missing metadata.exportedAt.');
  }
  if (!Array.isArray(json['tests'])) {
    return err('Not a LabCloud export: missing tests array.');
  }

  const meta = json['metadata'] as Record<string, unknown>;

  // Delta-mode exports cannot be re-imported — they only contain derived delta values
  if (typeof meta['chartType'] === 'string' && meta['chartType'] === 'delta') {
    return err('Delta-mode exports cannot be imported because specimen raw data is not included.');
  }

  const rawTests = json['tests'] as unknown[];
  if (rawTests.length === 0) {
    return err('Export file contains no tests.');
  }

  const tests: LabCloudExportEntry[] = [];
  for (const t of rawTests) {
    if (!isObject(t)) continue;
    const entry = t as Record<string, unknown>;
    if (typeof entry['testId'] !== 'string') continue;

    const baseline = parseExportMeasurement(entry['baseline']);
    const specimen = parseExportMeasurement(entry['specimen']);

    // Only include tests that have both phases
    if (!baseline || !specimen) continue;

    tests.push({
      testId: entry['testId'],
      sourceBaseline: typeof entry['sourceBaseline'] === 'string' ? entry['sourceBaseline'] : null,
      sourceSpecimen: typeof entry['sourceSpecimen'] === 'string' ? entry['sourceSpecimen'] : null,
      notes: typeof entry['notes'] === 'string' ? entry['notes'] : null,
      baseline,
      specimen,
    });
  }

  if (tests.length === 0) {
    return err('No importable tests found (each test needs both baseline and specimen data).');
  }

  return {
    ok: true,
    data: {
      filename,
      metadata: {
        exportedAt: meta['exportedAt'] as string,
        chartType: typeof meta['chartType'] === 'string' ? meta['chartType'] : 'bode',
        testCount: tests.length,
        channels: Array.isArray(meta['channels'])
          ? (meta['channels'] as unknown[]).filter((c): c is string => typeof c === 'string')
          : [],
      },
      tests,
    },
  };
}
